/* ------------------------------------------------------------------------
 * E&O mitigation record
 *
 * Lifted verbatim from index.html script block 8 (639 lines). An IIFE, so its
 * functions are private by design and it publishes what it wants on window.*
 * — no named exports, and importing it for the side effect is the point.
 * ---------------------------------------------------------------------- */


(function(){
'use strict';

// String-only escape rather than the page's usual createElement trick: these
// documents are also built headlessly by the test harness, which has no DOM.
function esc(s){
  return String(s==null?'':s)
    .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
    .replace(/"/g,'&quot;').replace(/'/g,'&#39;');
}
function longDate(d){ return d.toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'}); }
function isoDate(d){
  return d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0');
}
function slug(s){ return String(s||'Client').replace(/[^A-Za-z0-9]+/g,'-').replace(/^-+|-+$/g,'') || 'Client'; }

// ---------------------------------------------------------------------------
// Which sections are E&O-relevant
//
// Architecture v2 leaves this list open: "Aaron and Dayna are finalizing this
// list during their review of the new BOP, P&C, and EB analyses." What follows
// is the working assumption the document supplies, expressed as data so the
// final list is a one-place edit. Each entry reports on the analysis actually
// in hand and returns null when that analysis produced nothing for it, so the
// record never prints a mitigation block for a finding that does not exist.
// ---------------------------------------------------------------------------
/* Read a report section that the engine computes inline.
 *
 * 2B and 6 build their tables inside the report generator and keep nothing on
 * the analysis object, so there is no data to summarise — only rendered HTML.
 * Reading the section back off the page is not a workaround here: the record's
 * job is to REPRODUCE the sections of the analysis, so taking the text the
 * broker actually saw is the only thing that cannot drift from it. Returns ''
 * when the section is not on screen, and every caller treats that as "nothing
 * to report" rather than inventing a finding.
 */
function sectionText(prefix){
  try {
    var root = document.getElementById('fullReview');
    if(!root) return '';
    var heads = root.querySelectorAll('h3');
    for(var i=0;i<heads.length;i++){
      if(heads[i].textContent.trim().indexOf(prefix) === 0){
        var box = heads[i].closest('.report-section') || heads[i].parentNode;
        return (box.textContent || '').replace(/\s+/g,' ').trim();
      }
    }
  } catch(e){ /* no DOM, or the report is not rendered */ }
  return '';
}

/* The seven sections of the BOR analysis where E&O exposure lives.
 *
 * Fixed by Dayna's review of the Base Power report (annotated 2026-09-08):
 * 2B, 5C, 6, 7, 7C-Website, 9, and Section 11's cyber-liability finding — and
 * nothing else. The earlier working list carried four blocks that are not on
 * it (endorsement mismatches, BOP restriction endorsements, regulatory items,
 * standalone policies) and they are removed rather than kept alongside, which
 * is the call she made. "Exclusions that need client acknowledgment" is struck
 * out on the annotated copy: Section 7 replaces it, and Section 7 is the
 * cross-policy contradiction analysis, not the exclusion-alert list.
 *
 * Titles lead with the section number so a producer can hold the record beside
 * the report and match them line for line. Each entry returns null when the
 * analysis produced nothing for it, so the record never prints a mitigation
 * block for a finding that does not exist.
 */
var EO_SECTIONS = [
  {
    key: 'sec-2b-completeness',
    title: 'Section 2B — Policy program completeness',
    summarize: function(c){
      /* Reads what the report published when it rendered 2B. The DOM scrape
       * below stays as a fallback for a report drawn by an older engine, but
       * the data path is the one under test — the scrape only ever worked in a
       * real browser, so the one place it mattered was the one place nothing
       * could check it. */
      var s2b = window._spSection2B;
      if(s2b && typeof s2b.expected === 'number'){
        var b2 = [s2b.detected+' of '+s2b.expected+' expected coverage lines detected'];
        if((s2b.missing||[]).length) b2.push('not found: '+s2b.missing.join(', '));
        if(s2b.criticalAlert) b2.push('critical alert: '+s2b.criticalAlert);
        return b2.join(' · ') + ' · each line not found is a potential uninsured exposure until the broker confirms it sits on a policy outside this review';
      }
      var t = sectionText('2B.');
      if(!t) return null;
      var counts = t.match(/(\d+)\s+of\s+(\d+)\s+expected coverage lines detected/i);
      var missing = t.match(/Not Detected in Uploaded Policies:\s*(.*?)\s*Action Required/i);
      var bits = [];
      if(counts) bits.push(counts[1]+' of '+counts[2]+' expected coverage lines detected');
      if(missing && missing[1]) bits.push('not found: '+missing[1].trim());
      if(!bits.length) return null;
      return bits.join(' · ') + ' · each line not found is a potential uninsured exposure until the broker confirms it sits on a policy outside this review';
    }
  },
  {
    key: 'sec-5c-triggers',
    title: 'Section 5C — Trigger and retroactive date verification',
    summarize: function(c){
      var t = (c.data.triggers) || null;
      var rows = (t && t.byCoverage) || [];
      if(!rows.length) return null;
      var cm = rows.filter(function(r){ return /claims/i.test(r.trigger||''); });
      var bits = [rows.length+' liability coverage part'+(rows.length===1?'':'s')+' with a declared trigger'];
      if(cm.length){
        bits.push(cm.length+' claims-made: '+cm.map(function(r){
          return r.coverage+' (retro '+(r.retroDate || 'NOT STATED')+')'; }).join('; '));
        var noRetro = cm.filter(function(r){ return !r.retroDate; });
        if(noRetro.length) bits.push(noRetro.length+' claims-made part(s) with no retroactive date stated — prior acts may be unprotected');
      } else {
        bits.push('no claims-made parts detected');
      }
      if(t.misalignmentFlag) bits.push('retroactive dates are not aligned across the program');
      return bits.join(' · ');
    }
  },
  {
    key: 'sec-6-benchmarking',
    title: 'Section 6 — Industry benchmarking against statutory minimums and industry standard',
    summarize: function(c){
      // Published by the report when it rendered Section 6; the scrape below is
      // the fallback for an older engine, as with 2B.
      var s6 = window._spSection6;
      if(s6 && typeof s6.total === 'number' && s6.total){
        var by = s6.byStatus || {};
        var gaps6 = (by['HIGH GAP']||0) + (by['CRITICAL GAP']||0);
        var b6 = [(by['MEETS']||0)+' of '+s6.total+' line(s) meet the industry standard'];
        if(by['BELOW STANDARD']) b6.push(by['BELOW STANDARD']+' below standard');
        if(gaps6) b6.push(gaps6+' not found in the uploaded policies');
        if(by['VERIFY']) b6.push(by['VERIFY']+' requiring verification because limits were not parsed from the declarations');
        if((s6.belowOrMissing||[]).length){
          b6.push('specifically: '+s6.belowOrMissing.slice(0,6).map(function(r){ return r.type+' ['+r.status+']'; }).join('; '));
        }
        return b6.join(' · ') + ' · a limit recorded as below standard or unverified is the broker\'s to raise with the client and document here';
      }
      var t = sectionText('6.');
      if(!t) return null;
      var count = function(re){ var m = t.match(re); return m ? m.length : 0; };
      var below = count(/BELOW STANDARD/g);
      var gaps  = count(/HIGH GAP/g);
      var ver   = count(/VERIFY/g);
      var meets = count(/MEETS/g);
      if(!(below+gaps+ver+meets)) return null;
      var bits = [meets+' line(s) meet the industry standard'];
      if(below) bits.push(below+' below standard');
      if(gaps)  bits.push(gaps+' not found in the uploaded policies');
      if(ver)   bits.push(ver+' requiring verification because limits were not parsed from the declarations');
      return bits.join(' · ') + ' · a limit recorded as below standard or unverified is the broker\'s to raise with the client and document here';
    }
  },
  {
    key: 'sec-7-contradictions',
    title: 'Section 7 — Exclusion and contradiction analysis (cross-policy)',
    summarize: function(c){
      /* Recomputed the way the report computes it — the same alert list put
       * through the same grounding gate — so the record cannot claim a finding
       * the report did not make. An ungrounded alert is not a finding. */
      var alerts = c.exclusionAlerts || [];
      if(!alerts.length) return null;
      var grounded = [];
      alerts.forEach(function(a){
        if(a._demoted) return;
        var hit = null;
        try { hit = window.groundPCExclusion ? window.groundPCExclusion(a, c.data.pdfTexts || []) : null; }
        catch(e){ hit = null; }
        if(hit) grounded.push(a);
      });
      if(!grounded.length){
        return alerts.length+' exclusion alert(s) evaluated; none could be grounded in the uploaded policy wording, so none is reported as a finding — confirm against the actual forms';
      }
      var crit = grounded.filter(function(a){ return /CRITICAL|FATAL/i.test(a.impact||a.severity||''); });
      var bits = [grounded.length+' cross-policy conflict'+(grounded.length===1?'':'s')+' quoted from the policy language'];
      if(crit.length) bits.push(crit.length+' critical: '+crit.slice(0,3).map(function(a){ return a.coverage+' — '+a.exclusion; }).join('; '));
      else bits.push('highest: '+grounded.slice(0,3).map(function(a){ return a.coverage+' — '+a.exclusion; }).join('; '));
      return bits.join(' · ');
    }
  },
  {
    key: 'sec-7c-website',
    title: 'Section 7C — Website and operational risk analysis',
    summarize: function(c){
      var r = c.data.websiteRisks || [];
      var meta = window._siteFetchMeta;
      if(!r.length){
        /* The section now always renders in the report, so the record follows
         * it: "read and found nothing" and "never read" are different
         * statements and the producer needs the right one on the record. */
        if(meta && meta.host){
          return 'the public site ('+meta.host+') was read and no operational risk pattern was evidenced in it — nothing to acknowledge, recorded so the file shows the check was made';
        }
        return 'no website text was read'+(window._siteFetchError ? ' ('+window._siteFetchError+')' : '')
          + ' — this section was not evidenced, which is not the same as finding no risk. Supply a reachable URL and re-run before relying on it';
      }
      return r.length+' operational risk pattern'+(r.length===1?'':'s')+' found on the public site'
        + (meta && meta.host ? ' ('+meta.host+')' : '') + ': '
        + r.slice(0,5).map(function(x){ return x.category+' — '+x.risk; }).join('; ')
        + (r.length>5 ? ' (+'+(r.length-5)+' more)' : '');
    }
  },
  {
    key: 'sec-9-gaps',
    title: 'Section 9 — Priority gap analysis and recommendations',
    summarize: function(c){
      var gaps = c.gaps.filter(function(g){ return g.priority==='CRITICAL' || g.priority==='HIGH'; });
      if(!gaps.length) return null;
      var crit = gaps.filter(function(g){ return g.priority==='CRITICAL'; }).length;
      return gaps.length+' priority gap'+(gaps.length===1?'':'s')+' ('+crit+' critical): '
        + gaps.slice(0,6).map(function(g){ return g.type; }).join(', ')
        + (gaps.length>6 ? ', +'+(gaps.length-6)+' more' : '')
        + ' · each is a recommendation the client accepts or declines below';
    }
  },
  {
    key: 'sec-11-cyber',
    title: 'Section 11 — Cyber liability coverage',
    summarize: function(c){
      var cov = c.data.detectedCoverages || [];
      var hasCyber = cov.some(function(x){ return /cyber/i.test(x); });
      var lim = (c.data.declLimits && (c.data.declLimits['Cyber Liability'] || c.data.declLimits.cyber)) || null;
      if(hasCyber){
        return 'cyber liability detected'
          + (lim ? ' ('+String(lim).replace(/\s+/g,' ').trim().slice(0,140)+')' : ' — limits not parsed from the declarations')
          + ' · confirm the limit, retention and scope (first-party and third-party) are adequate for this sector, and whether social engineering, funds transfer fraud and regulatory proceeding cover are included. Record what was discussed';
      }
      return 'no cyber liability coverage was detected in the uploaded policies — the exposure exists regardless of whether a policy answers it. Record the recommendation and the client\'s decision';
    }
  }
];

// ---------------------------------------------------------------------------
// Context — read the analysis that is actually on screen. All three lines
// (P&C, EB, BOP) funnel into one shape so the record is line-agnostic.
// ---------------------------------------------------------------------------
function buildContext(){
  var data = (typeof currentAnalysis !== 'undefined' && currentAnalysis) ? currentAnalysis : (window._lastFormData || null);
  if(!data) return null;
  var bop = window._bopAnalysis;
  var isBop = !!(bop && bop.detection && bop.detection.is_bop);
  var S = (isBop && bop.sections) ? bop.sections : {};
  var alerts = [];
  try { alerts = getExclusionAlerts(data.detectedCoverages || [], data.vertical || '', data.endorsements) || []; }
  catch(e){ alerts = []; }
  /* The record is for P&C and BOP only.
   *
   * Dayna's annotation on the Base Power review: "NO E&O FOR EB — P&C &
   * BOP-only". The seven sections it reproduces are P&C/BOP report sections;
   * an EB review does not produce 2B, 5C, 6, 7, 7C or 11, so a record built
   * from an EB analysis would be a form with a producer's signature on it and
   * almost nothing above the signature. Refusing is the honest outcome.
   */
  var lob = String(data.lob || '').toLowerCase();
  return {
    data: data,
    lob: lob,
    isBop: isBop,
    eoAvailable: isBop || lob === 'pc' || lob === 'bop',
    bop: isBop ? bop : null,
    gaps: window._spRecommendedGaps || [],
    bopNotCovered: S.s5_not_covered || [],
    bopNotElected: S.s4_addons_not_elected || [],
    bopRestrictions: S.s6_restrictions || [],
    bopRegulatory: S.s8_regulatory || null,
    exclusionAlerts: alerts,
    lineLabel: isBop ? 'BOP BOR Analysis' : (data.lob === 'pc' ? 'P&C BOR Analysis' : 'EB BOR Analysis')
  };
}

// Every coverage the BOR analysis recommends the client add. Deduplicated by
// coverage name — a coverage flagged both as a BOP standalone and as a priority
// gap is one decision for the client, not two.
function recommendedCoverages(c){
  var out = [], seen = {};
  function add(name, why, priority, source){
    var key = String(name||'').trim().toLowerCase();
    if(!key || seen[key]) return;
    seen[key] = true;
    out.push({ coverage: String(name).trim(), why: why||'', priority: priority||'MODERATE', source: source||'' });
  }
  c.bopNotCovered.forEach(function(r){
    if(r.criticality === 'INFO') return;
    add(r.coverage, r.recommendation || r.rationale, r.criticality, 'Section 5 — NOT COVERED / standalone required');
  });
  c.bopNotElected.forEach(function(a){
    add(a.addon, a.why || a.action, a.criticality, 'Section 4 — available add-on not elected');
  });
  /* Section 9, not 8.
   *
   * These come from window._spRecommendedGaps, which both the P&C and the EB
   * report populate under "9. Priority Gap Analysis & Recommendations".
   * Section 8 is the Federal Compliance Penalty Structure, which recommends
   * nothing — so every recommendation on the record cited a section it did not
   * come from, and a producer following the citation landed on a penalties
   * table. An off-by-one left from before 5C and 7C were inserted.
   *
   * The Section 4 and Section 5 labels above are BOP-review section numbers
   * (R3 and R4) and are correct; they belong to a different document, which is
   * why they do not shift with the P&C numbering. */
  c.gaps.forEach(function(g){
    if(g.priority === 'VERIFY') return;
    add(g.type, g.action, g.priority, 'Section 9 — priority gap analysis');
  });
  var rank = { CRITICAL:0, HIGH:1, MODERATE:2, MEDIUM:2, LOW:3 };
  out.sort(function(a,b){ return (rank[a.priority]==null?4:rank[a.priority]) - (rank[b.priority]==null?4:rank[b.priority]); });
  return out;
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------
function openRecordModal(){
  var c = buildContext();
  if(!c){ showToast('Run an analysis first — the E&O Mitigation Record is generated from a BOR analysis.', 'error'); return; }
  if(!c.eoAvailable){
    showToast('The E&O Mitigation Record covers Property & Casualty and BOP reviews. An Employee Benefits review does not produce the sections it documents.', 'error');
    return;
  }
  var recs = recommendedCoverages(c);
  var now = new Date();
  var existing = document.getElementById('spEoModal');
  if(existing) existing.remove();

  var h = '';
  h += '<div id="spEoModal" style="position:fixed;inset:0;background:rgba(0,0,0,.55);z-index:9998;display:flex;align-items:flex-start;justify-content:center;overflow:auto;padding:24px 12px;">';
  h += '<div role="dialog" aria-modal="true" aria-labelledby="spEoModalTitle" style="background:#fff;border-radius:10px;max-width:900px;width:100%;box-shadow:0 12px 48px rgba(0,0,0,.35);">';
  h += '<div style="background:#003366;color:#fff;padding:14px 18px;border-radius:10px 10px 0 0;display:flex;justify-content:space-between;align-items:center;">';
  h += '<div><div id="spEoModalTitle" style="font-weight:700;font-size:16px;">Generate E&amp;O Mitigation Record</div>';
  h += '<div style="font-size:11px;opacity:.85;">From the ' + esc(c.lineLabel) + ' for ' + esc(c.data.policyholderName || '—') + '</div></div>';
  h += '<button type="button" onclick="document.getElementById(\'spEoModal\').remove()" style="background:none;border:none;color:#fff;font-size:24px;line-height:1;cursor:pointer;padding:0 4px;" aria-label="Close">&times;</button>';
  h += '</div>';
  h += '<div style="padding:16px 18px;">';

  // -- Header block inputs
  h += '<div style="font-weight:700;color:#003366;font-size:13px;margin-bottom:6px;">Record header</div>';
  h += '<div style="display:grid;grid-template-columns:1fr 1fr;gap:10px 14px;">';
  h += fld('spEoClient','Client name', esc(c.data.policyholderName||''));
  h += fld('spEoDba','DBA (if different)','');
  h += fld('spEoAgency','Agency','');
  h += fld('spEoProducer','Producer','');
  h += fld('spEoAnalysisDate','Date of BOR analysis', isoDate(now), 'date');
  h += fld('spEoPolicyNumber','Policy number this record is filed against',
        esc((c.bop && c.bop.sections && c.bop.sections.s1_header && c.bop.sections.s1_header.policy_number) || ''));
  h += '</div>';

  // -- Delivery
  h += '<div style="font-weight:700;color:#003366;font-size:13px;margin:16px 0 6px;">Delivery</div>';
  h += '<label style="display:block;font-size:12px;margin:3px 0;"><input type="radio" name="spEoDelivery" value="separate" checked> ';
  h += 'Download as a separate PDF <span style="color:#777;">(default — keeps the analysis clean and the record editable)</span></label>';
  h += '<label style="display:block;font-size:12px;margin:3px 0;"><input type="radio" name="spEoDelivery" value="appended"> ';
  h += 'Print as the last page of the analysis PDF</label>';

  // -- Recommended coverages / accept-decline
  h += '<div style="font-weight:700;color:#003366;font-size:13px;margin:16px 0 6px;">Recommended coverages — mark the client conversation</div>';
  if(recs.length){
    h += '<div style="max-height:280px;overflow:auto;border:1px solid #dde3ea;border-radius:6px;">';
    h += '<table style="width:100%;border-collapse:collapse;font-size:12px;">';
    h += '<thead><tr style="background:#e8eef5;position:sticky;top:0;">';
    h += '<th style="text-align:left;padding:5px 8px;">Coverage recommended</th>';
    h += '<th style="text-align:left;padding:5px 8px;">Why recommended</th>';
    h += '<th style="padding:5px 8px;width:60px;">Accept</th><th style="padding:5px 8px;width:66px;">Decline</th>';
    h += '<th style="padding:5px 8px;width:76px;">Undecided</th></tr></thead><tbody>';
    recs.forEach(function(r,i){
      h += '<tr data-sp-rec="'+i+'">';
      h += '<td style="padding:5px 8px;border-top:1px solid #eef1f5;"><strong>'+esc(r.coverage)+'</strong>'
         + '<div style="font-size:10px;color:#888;">'+esc(r.priority)+' · '+esc(r.source)+'</div></td>';
      h += '<td style="padding:5px 8px;border-top:1px solid #eef1f5;font-size:11px;color:#444;">'+esc(r.why)+'</td>';
      h += '<td style="padding:5px 8px;border-top:1px solid #eef1f5;text-align:center;"><input type="radio" name="spEoDec'+i+'" value="accept"></td>';
      h += '<td style="padding:5px 8px;border-top:1px solid #eef1f5;text-align:center;"><input type="radio" name="spEoDec'+i+'" value="decline"></td>';
      h += '<td style="padding:5px 8px;border-top:1px solid #eef1f5;text-align:center;"><input type="radio" name="spEoDec'+i+'" value="undecided" checked></td>';
      h += '</tr>';
    });
    h += '</tbody></table></div>';
    h += '<div style="font-size:11px;color:#6d4c00;background:#fff8e1;border-left:3px solid #f9a825;padding:6px 10px;margin-top:8px;">';
    h += 'Any coverage marked <strong>Decline</strong> requires a signed Recommended Coverage Declination Form from the client. ';
    h += 'Without the signed declination the E&amp;O defense is incomplete — the record will say so, and the form is generated for you.';
    h += '</div>';
  } else {
    h += '<div style="font-size:12px;color:#777;border:1px dashed #ccd;border-radius:6px;padding:10px;">';
    h += 'This analysis recommended no additional coverages. The record will still document the sections reviewed.</div>';
  }

  h += '<div style="display:flex;gap:8px;justify-content:flex-end;margin-top:18px;">';
  h += '<button type="button" class="btn btn-outline btn-sm" onclick="document.getElementById(\'spEoModal\').remove()">Cancel</button>';
  h += '<button type="button" class="btn btn-accent btn-sm" id="spEoGenerateBtn">Generate record</button>';
  h += '</div>';
  h += '</div></div></div>';

  var wrap = document.createElement('div');
  wrap.innerHTML = h;
  document.body.appendChild(wrap.firstChild);
  document.getElementById('spEoGenerateBtn').addEventListener('click', function(){ submitRecord(c, recs); });
  document.getElementById('spEoModal').addEventListener('keydown', function(ev){
    if(ev.key === 'Escape') document.getElementById('spEoModal').remove();
  });
  var first = document.getElementById('spEoClient');
  if(first) first.focus();
}

function fld(id, label, value, type){
  return '<label style="font-size:11px;color:#555;display:block;">'+esc(label)
    + '<input id="'+id+'" type="'+(type||'text')+'" value="'+(value||'')+'" '
    + 'style="width:100%;margin-top:2px;padding:6px 8px;border:1px solid #cfd6de;border-radius:4px;font-size:12px;"></label>';
}

function val(id){ var el = document.getElementById(id); return el ? el.value.trim() : ''; }

function submitRecord(c, recs){
  var now = new Date();
  var meta = {
    client: val('spEoClient') || c.data.policyholderName || '—',
    dba: val('spEoDba'),
    agency: val('spEoAgency'),
    producer: val('spEoProducer'),
    analysisDate: val('spEoAnalysisDate') || isoDate(now),
    policyNumber: val('spEoPolicyNumber'),
    generatedOn: now,
    line: c.lineLabel
  };
  var delivery = (document.querySelector('input[name="spEoDelivery"]:checked') || {}).value || 'separate';
  var decisions = recs.map(function(r,i){
    var picked = document.querySelector('input[name="spEoDec'+i+'"]:checked');
    return { rec: r, decision: picked ? picked.value : 'undecided' };
  });
  document.getElementById('spEoModal').remove();

  var html = buildRecordHtml(c, meta, decisions);
  var filename = slug(meta.client) + '_EO-Mitigation-Record_' + isoDate(now);

  if(delivery === 'appended'){
    appendToAnalysisPrint(html, filename);
  } else {
    printDocument(filename, html);
  }
  window._spEoLast = { meta: meta, decisions: decisions, context: c };
  var declined = decisions.filter(function(d){ return d.decision === 'decline'; });
  if(declined.length){
    showToast(declined.length + ' coverage' + (declined.length===1?'':'s') + ' declined — generate the signed Declination Form to complete the E&O file.', 'warning');
  } else {
    showToast('E&O Mitigation Record generated.', 'success');
  }
}

// ---------------------------------------------------------------------------
// The record document
// ---------------------------------------------------------------------------
function buildRecordHtml(c, meta, decisions){
  var h = '';
  h += '<div class="eo-doc">';
  h += '<h1>E&amp;O Mitigation Record</h1>';
  h += '<div class="eo-sub">Producer remediation and client coverage decisions arising from the ' + esc(meta.line) + '</div>';

  // -- Header block
  h += '<table class="eo-header"><tbody>';
  h += hrow('Client', esc(meta.client) + (meta.dba ? ' <span class="muted">d/b/a ' + esc(meta.dba) + '</span>' : ''));
  h += hrow('Agency', esc(meta.agency || '________________________'));
  h += hrow('Producer', esc(meta.producer || '________________________'));
  h += hrow('Date of BOR analysis', esc(meta.analysisDate));
  h += hrow('Date of record generation', esc(longDate(meta.generatedOn)));
  if(meta.policyNumber) h += hrow('Policy number', esc(meta.policyNumber));
  h += '</tbody></table>';

  h += '<p class="eo-purpose">This record documents what the producer did after receiving the BOR analysis: what gaps were fixed, '
     + 'what exclusions were discussed, and what recommended coverages the client accepted or declined. Together with the BOR analysis '
     + 'and any signed declinations, it forms the agency&rsquo;s three-layer E&amp;O defense.</p>';

  // -- Section-by-section mitigation notes
  h += '<h2>Section-by-section mitigation notes</h2>';
  h += '<p class="eo-note">Only the sections of the BOR analysis where E&amp;O exposure lives are reproduced below. '
     + 'Record the mitigation action taken for each.</p>';
  var printed = 0;
  EO_SECTIONS.forEach(function(sec){
    var summary;
    try { summary = sec.summarize(c); } catch(e){ summary = null; }
    if(!summary) return;
    printed++;
    h += '<div class="eo-sec">';
    h += '<div class="eo-sec-title">' + printed + '. ' + esc(sec.title) + '</div>';
    h += '<div class="eo-finding"><span class="eo-label">Finding:</span> ' + esc(summary) + '</div>';
    h += '<div class="eo-action"><span class="eo-label">Mitigation action taken:</span>'
       + '<div class="eo-lines"><div></div><div></div><div></div></div></div>';
    h += '<div class="eo-byline">By: ______________________________ &nbsp;&nbsp; Date: ______________</div>';
    h += '</div>';
  });
  if(!printed){
    h += '<div class="eo-sec"><div class="eo-finding">The BOR analysis produced no findings in the E&amp;O-relevant sections.</div></div>';
  }

  // -- Recommended coverages
  h += '<h2>Recommended coverages — client decision</h2>';
  if(decisions.length){
    h += '<table class="eo-table"><thead><tr>'
       + '<th>Coverage recommended</th><th>Why recommended</th><th class="c">Accept</th><th class="c">Decline</th></tr></thead><tbody>';
    decisions.forEach(function(d){
      var acc = d.decision === 'accept' ? '&#10003;' : '&#9744;';
      var dec = d.decision === 'decline' ? '&#10003;' : '&#9744;';
      h += '<tr' + (d.decision==='decline' ? ' class="declined"' : '') + '>';
      h += '<td><strong>' + esc(d.rec.coverage) + '</strong><div class="muted small">' + esc(d.rec.priority) + '</div></td>';
      h += '<td class="small">' + esc(d.rec.why) + '</td>';
      h += '<td class="c box">' + acc + '</td><td class="c box">' + dec + '</td></tr>';
    });
    h += '</tbody></table>';
  } else {
    h += '<p class="eo-note">No additional coverages were recommended by this analysis.</p>';
  }

  // -- Declination trigger
  var declined = decisions.filter(function(d){ return d.decision === 'decline'; });
  var undecided = decisions.filter(function(d){ return d.decision === 'undecided'; });
  if(declined.length){
    h += '<div class="eo-trigger">';
    h += '<div class="eo-trigger-title">Signed declination required — E&amp;O defense is incomplete without it</div>';
    h += '<p>The client declined the following recommended coverage' + (declined.length===1?'':'s') + '. '
       + 'A signed <em>Recommended Coverage Declination Form</em> must be obtained from the client and attached to this record:</p>';
    h += '<ul>' + declined.map(function(d){ return '<li>' + esc(d.rec.coverage) + '</li>'; }).join('') + '</ul>';
    h += '<p class="eo-attach">Attached: &#9744; Signed Recommended Coverage Declination Form dated ______________</p>';
    h += '</div>';
  }
  if(undecided.length){
    h += '<div class="eo-open"><strong>Open items:</strong> ' + undecided.length + ' recommended coverage'
       + (undecided.length===1?' has':'s have') + ' no recorded client decision yet ('
       + esc(undecided.map(function(d){ return d.rec.coverage; }).join(', ')) + '). '
       + 'Follow up and update this record.</div>';
  }

  // -- Sign-off
  h += '<h2>Producer attestation</h2>';
  h += '<p class="eo-note">I confirm that the findings above were reviewed with the client, that each recommended coverage was '
     + 'presented, and that the client&rsquo;s decisions are recorded accurately.</p>';
  h += '<div class="eo-signrow">';
  h += '<div><div class="eo-sigline"></div>Producer signature</div>';
  h += '<div><div class="eo-sigline short"></div>Date</div>';
  h += '</div>';

  h += '<div class="eo-foot">SentriPol&trade; E&amp;O Mitigation Record &middot; generated ' + esc(longDate(meta.generatedOn))
     + ' from the ' + esc(meta.line) + '. This record is a documentation aid, not legal advice. '
     + 'Retain it with the referenced policy record.</div>';
  h += '</div>';
  return h;
}

function hrow(k,v){ return '<tr><th>' + k + '</th><td>' + v + '</td></tr>'; }

// ---------------------------------------------------------------------------
// The Recommended Coverage Declination Form (v1)
// Reproduces the attorney-review draft, prefilled from the record so the
// producer is not retyping coverage names the analysis already produced.
// ---------------------------------------------------------------------------
function buildDeclinationHtml(meta, declinedRows){
  var rows = (declinedRows && declinedRows.length) ? declinedRows : [];
  var blanks = Math.max(0, 4 - rows.length);
  var h = '';
  h += '<div class="eo-doc decl">';
  h += '<h1 class="center">RECOMMENDED COVERAGE DECLINATION FORM</h1>';
  h += '<div class="eo-sub center">Client acknowledgment of coverage recommendations and informed declination</div>';

  h += '<h2>Purpose of this form</h2>';
  h += '<p>This form documents insurance coverage(s) that were recommended to the below-named Client by the below-named Broker '
     + 'and Agency, and which the Client has elected to decline. Its purpose is to establish a written record of the recommendation, '
     + 'the discussion of associated risks, the Client&rsquo;s informed decision to decline, and the Client&rsquo;s acknowledgment that any loss '
     + 'falling within the scope of the declined coverage will not be covered by the Client&rsquo;s insurance program.</p>';

  h += '<h2>Client and Broker information</h2>';
  h += '<table class="decl-info"><tbody>';
  h += drow('Client Organization', esc(meta.client || ''));
  h += drow('Client Representative', '');
  h += drow('Title / Role', '');
  h += drow('Broker Name', esc(meta.producer || ''));
  h += drow('Agency', esc(meta.agency || ''));
  h += drow('Date of Recommendation', esc(meta.analysisDate || ''));
  h += '</tbody></table>';

  h += '<h2>Coverages recommended and declined</h2>';
  h += '<p>The Broker has recommended the following coverage(s) after reviewing the Client&rsquo;s current insurance program. '
     + 'The Client has elected to decline each coverage marked below and has initialed each row to confirm the declination is '
     + 'informed and voluntary.</p>';
  h += '<table class="eo-table decl-table"><thead><tr>'
     + '<th>Coverage recommended</th><th>Reason recommended (brief)</th><th class="c initials">Client initials</th>'
     + '</tr></thead><tbody>';
  rows.forEach(function(r){
    h += '<tr><td>' + esc(r.coverage) + '</td><td class="small">' + esc(r.why) + '</td><td class="c">__________</td></tr>';
  });
  for(var i=0;i<blanks;i++){
    h += '<tr><td>_______________________</td><td>_________________________________</td><td class="c">__________</td></tr>';
  }
  h += '</tbody></table>';

  h += '<h2>Client acknowledgment</h2>';
  h += '<p>By signing below, the Client acknowledges and agrees to each of the following:</p>';
  h += '<ol class="decl-ack">';
  h += '<li>The Broker and Agency identified above have recommended each of the coverages listed in the table above.</li>';
  h += '<li>The Client has been informed of the risks and potential financial exposure of declining each recommended coverage, '
     + 'including the possibility that a loss may occur which would have been covered had the recommended coverage been in place.</li>';
  h += '<li>The Client has had the opportunity to ask questions and has received sufficient information to make an informed decision.</li>';
  h += '<li>After consideration, the Client has voluntarily elected to decline each coverage marked above.</li>';
  h += '<li>The Client releases the Broker and Agency from liability for any loss, claim, or damage that would have been covered '
     + 'by the declined coverage(s) had they been in place.</li>';
  h += '<li>This declination may be reversed at any time by the Client submitting a written request to the Agency and executing '
     + 'the necessary policy applications.</li>';
  h += '</ol>';

  h += '<h2>Signatures</h2>';
  h += '<div class="decl-sign">';
  h += sigBlock('Client (authorized representative)', 'Date', true);
  h += sigBlock('Broker (agent of record)', 'Date', true);
  h += '<div class="decl-sig-row"><div class="grow"><div class="lbl">Applicable policy number (policy this declination is filed against)</div>'
     + '<div class="eo-sigline">' + esc(meta.policyNumber || '') + '</div></div>'
     + '<div class="dt"><div class="lbl">Effective date</div><div class="eo-sigline short"></div></div></div>';
  h += '</div>';

  h += '<div class="eo-foot"><strong>Disclaimer</strong><br>'
     + 'This form is provided by SentriPol as a general template for agency use. It does not constitute legal advice, and SentriPol '
     + 'makes no representation regarding its sufficiency, enforceability, or fitness for any particular use. Each agency is '
     + 'responsible for having this form reviewed by its own legal counsel and adapted to comply with applicable state law, E&amp;O '
     + 'carrier requirements, and the agency&rsquo;s own business practices. Use of this form does not create an attorney-client '
     + 'relationship with SentriPol.<br><br>'
     + 'Retention: Retain a signed original with the referenced policy record and attach a copy to the E&amp;O Mitigation Record for this Client.</div>';
  h += '</div>';
  return h;
}

function drow(k,v){ return '<tr><th>' + k + '</th><td class="line">' + v + '</td></tr>'; }

function sigBlock(who, dateLabel){
  return '<div class="decl-sig-row">'
    + '<div class="grow"><div class="lbl">' + who + '</div><div class="eo-sigline"></div>'
    + '<div class="printed">Printed name: _______________________________</div></div>'
    + '<div class="dt"><div class="lbl">' + dateLabel + '</div><div class="eo-sigline short"></div></div></div>';
}

// Public: print the declination form for the coverages declined in the last
// record (or standalone, with blank rows, when no record has been generated).
function openDeclinationForm(){
  // Same line restriction as the record: the declination form records
  // decisions made against it, so it cannot outlive its own record.
  var ctx = buildContext();
  if(ctx && !ctx.eoAvailable){
    showToast('The declination form accompanies the E&O Mitigation Record, which covers Property & Casualty and BOP reviews only.', 'error');
    return;
  }
  var last = window._spEoLast;
  var meta, rows;
  if(last){
    meta = last.meta;
    rows = last.decisions.filter(function(d){ return d.decision === 'decline'; })
                         .map(function(d){ return d.rec; });
  } else {
    var c = buildContext();
    meta = {
      client: (c && c.data.policyholderName) || '',
      agency: '', producer: '', policyNumber: '',
      analysisDate: isoDate(new Date()), generatedOn: new Date()
    };
    rows = [];
  }
  var filename = slug(meta.client) + '_Recommended-Coverage-Declination-Form_' + isoDate(new Date());
  printDocument(filename, buildDeclinationHtml(meta, rows));
}

// ---------------------------------------------------------------------------
// Output
// ---------------------------------------------------------------------------
function docStyles(){
  return '@page { margin: 0.75in; size: letter; }'
  + '* { box-sizing: border-box; margin: 0; padding: 0; }'
  + 'body { font-family: Inter, -apple-system, system-ui, sans-serif; font-size: 12px; color: #1a1a2e; line-height: 1.5; }'
  + '.eo-doc h1 { font-size: 21px; color: #003366; margin-bottom: 2px; }'
  + '.eo-doc h2 { font-size: 14px; color: #003366; border-bottom: 1px solid #003366; padding-bottom: 3px; margin: 20px 0 8px; }'
  + '.eo-sub { font-size: 12px; color: #666; margin-bottom: 14px; }'
  + '.center { text-align: center; }'
  + '.muted { color: #777; font-weight: 400; }'
  + '.small { font-size: 11px; }'
  + 'p { margin-bottom: 8px; }'
  + '.eo-header { width: 100%; border-collapse: collapse; margin: 10px 0 4px; }'
  + '.eo-header th { text-align: left; width: 200px; padding: 4px 8px 4px 0; color: #555; font-weight: 600; font-size: 11px; vertical-align: top; }'
  + '.eo-header td { padding: 4px 0; border-bottom: 1px solid #e6eaee; }'
  + '.eo-purpose { font-size: 11px; color: #444; background: #f5f8fb; border-left: 3px solid #003366; padding: 8px 12px; margin: 12px 0; }'
  + '.eo-note { font-size: 11px; color: #666; margin-bottom: 10px; }'
  + '.eo-sec { border: 1px solid #dde3ea; border-radius: 6px; padding: 10px 12px; margin: 10px 0; page-break-inside: avoid; }'
  + '.eo-sec-title { font-weight: 700; color: #003366; font-size: 12px; margin-bottom: 5px; }'
  + '.eo-label { font-weight: 700; font-size: 11px; color: #444; }'
  + '.eo-finding { font-size: 11px; color: #333; margin-bottom: 8px; }'
  + '.eo-lines div { border-bottom: 1px solid #b9c3ce; height: 20px; margin-top: 6px; }'
  + '.eo-byline { font-size: 10px; color: #777; margin-top: 10px; }'
  + '.eo-table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 11px; page-break-inside: auto; }'
  + '.eo-table th { background: #003366; color: #fff; padding: 6px 8px; text-align: left; font-weight: 600; }'
  + '.eo-table td { padding: 6px 8px; border: 1px solid #ccd3da; vertical-align: top; }'
  + '.eo-table .c { text-align: center; }'
  + '.eo-table .box { font-size: 15px; }'
  + '.eo-table tr.declined td { background: #fff5f5; }'
  + '.eo-trigger { border: 2px solid #b71c1c; border-radius: 6px; padding: 10px 14px; margin: 14px 0; background: #fff7f7; page-break-inside: avoid; }'
  + '.eo-trigger-title { font-weight: 700; color: #b71c1c; font-size: 12px; margin-bottom: 5px; }'
  + '.eo-trigger ul { margin: 6px 0 6px 20px; font-size: 11px; }'
  + '.eo-attach { font-size: 11px; font-weight: 600; margin-top: 8px; }'
  + '.eo-open { font-size: 11px; background: #fff8e1; border-left: 3px solid #f9a825; padding: 8px 12px; margin: 10px 0; }'
  + '.eo-signrow { display: flex; gap: 32px; margin-top: 18px; font-size: 11px; color: #555; }'
  + '.eo-signrow > div { flex: 1; }'
  + '.eo-signrow > div:last-child { flex: 0 0 160px; }'
  + '.eo-sigline { border-bottom: 1px solid #333; height: 22px; margin-bottom: 3px; }'
  + '.eo-sigline.short { width: 150px; }'
  + '.eo-foot { font-size: 10px; color: #777; margin-top: 26px; padding-top: 10px; border-top: 1px solid #ccc; }'
  + '.decl-info { width: 100%; border-collapse: collapse; margin: 8px 0; }'
  + '.decl-info th { text-align: left; width: 190px; padding: 6px 8px 6px 0; font-weight: 400; color: #333; font-size: 12px; vertical-align: bottom; }'
  + '.decl-info td.line { border-bottom: 1px solid #333; padding: 6px 4px; }'
  + '.decl-table .initials { width: 92px; }'
  + '.decl-table td { height: 30px; }'
  + '.decl-ack { margin: 8px 0 8px 22px; font-size: 11px; }'
  + '.decl-ack li { margin-bottom: 6px; }'
  + '.decl-sign { margin-top: 10px; }'
  + '.decl-sig-row { display: flex; gap: 30px; margin-bottom: 16px; page-break-inside: avoid; }'
  + '.decl-sig-row .grow { flex: 1; }'
  + '.decl-sig-row .dt { flex: 0 0 160px; }'
  + '.decl-sig-row .lbl { font-size: 11px; color: #555; margin-bottom: 3px; }'
  + '.decl-sig-row .printed { font-size: 11px; color: #333; }'
  + '@media print { th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }'
  + ' .eo-trigger, .eo-table th { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }';
}

// The browser offers the document title as the default "Save as PDF" filename,
// which is how the spec's [Client]_EO-Mitigation-Record_[YYYY-MM-DD].pdf
// convention is honoured without a PDF library in the loop.
function printDocument(filename, bodyHtml, noAutoPrint){
  var w = window.open('', '_blank');
  if(!w){ showToast('Pop-up blocked. Allow pop-ups for this site to save the document as a PDF.', 'warning'); return; }
  w.document.write('<!DOCTYPE html><html><head><meta charset="UTF-8"><title>' + esc(filename)
    + '</title><style>' + docStyles() + '</style></head><body>' + bodyHtml);
  if(!noAutoPrint){
    w.document.write('<div class="eo-note" style="margin-top:18px;">Tip: in the print dialog choose &ldquo;Save as PDF&rdquo;. '
      + 'The suggested filename is <strong>' + esc(filename) + '.pdf</strong>.</div>');
  }
  w.document.write('</body></html>');
  w.document.close();
  setTimeout(function(){ w.print(); }, 500);
}

// "Print the record as the last page of the analysis PDF" — reuse the report's
// own print path so the analysis renders exactly as Print/Save as PDF does,
// with the record appended after it.
function appendToAnalysisPrint(recordHtml, filename){
  var activeTab = document.querySelector('.tab-content.active');
  if(!activeTab){ printDocument(filename, recordHtml); return; }
  var holder = document.createElement('div');
  holder.id = 'spEoAppendedRecord';
  holder.style.pageBreakBefore = 'always';
  holder.innerHTML = '<style>' + docStyles().replace(/@page[^}]*}/, '') + '</style>' + recordHtml;
  activeTab.appendChild(holder);
  try {
    handlePrint();
  } finally {
    setTimeout(function(){ var el = document.getElementById('spEoAppendedRecord'); if(el) el.remove(); }, 1500);
  }
}

/* Show the two E&O controls only on the lines the record covers.
 *
 * The gates in openRecordModal and openDeclinationForm are the enforcement;
 * this is so nobody clicks a button that can only tell them no. Called after
 * an analysis renders — idempotent, and safe when the buttons are absent.
 */
function spSyncEoButtons(){
  var ctx = null;
  try { ctx = buildContext(); } catch(e){ ctx = null; }
  var show = !ctx || ctx.eoAvailable;   // no analysis yet: leave as authored
  ['eoRecordBtn','declinationBtn'].forEach(function(id){
    var el = document.getElementById(id);
    if(!el) return;
    el.style.display = show ? '' : 'none';
    if(show) el.removeAttribute('aria-hidden'); else el.setAttribute('aria-hidden','true');
  });
}

window.spSyncEoButtons = spSyncEoButtons;
window.spOpenEoRecord = openRecordModal;
window.spOpenDeclinationForm = openDeclinationForm;
// Exposed for the section-list swap when Aaron and Dayna finalise it, and so the
// document builders can be exercised headlessly by tests/eo-mitigation-record.test.js.
window._spEoSections = EO_SECTIONS;
window._spEoInternals = {
  EO_SECTIONS: EO_SECTIONS,
  buildContext: buildContext,
  recommendedCoverages: recommendedCoverages,
  buildRecordHtml: buildRecordHtml,
  buildDeclinationHtml: buildDeclinationHtml
};
})();
